// Control Center (design_handoff_control_center): reine Logik, unverändert aus SuperAdminDashboard /
// OrdnerWechselListe herausgelöst – Portal-Module, Knopf-Ableitung und Filter des Ordner-Wechsels, Schwellen-Stepper.
import type { WechselEintrag } from '../wechsel/api';

/** Portal-Module mit Standard „aktiv“, bis portal_modules geladen ist (Texte wie bisher im Code). */
export const PORTAL_MODULE: readonly { id: string; name: string; description: string }[] = [
  { id: 'dashboard', name: 'Dashboard', description: 'Kern-Analytik & Metriken' },
  { id: 'objekte', name: 'Objekte', description: 'Immobilien-Bestand' },
  { id: 'mieter', name: 'Mieter', description: 'Bewohner-Verwaltung' },
  { id: 'eigentuemer', name: 'Eigentümer', description: 'Mandanten-Kontrolle' },
  { id: 'rechnungen', name: 'Rechnungen', description: 'Finanzfluss & Belege' },
  { id: 'dokumente', name: 'Dokumente', description: 'Archiv & Verträge' },
  { id: 'team', name: 'Team', description: 'Personal-Berechtigungen' },
  { id: 'chat', name: 'KI-Assistent', description: 'Bot & LLM Support' },
];

/**
 * Zeilen für das upsert von portal_modules beim „Speichern“ (Entscheidung 18.10.2026): Ein- und Ausschalten
 * ändert nur is_active – steht ein Modul schon in der Datenbank, bleibt dessen Name (z. B. „Finanzen“) erhalten;
 * den Namen aus der Code-Liste bekommt nur ein Modul, das dort noch fehlt.
 */
export function portalUpdates(
  module: readonly { id: string; name: string; active: boolean }[],
  dbNamen: Readonly<Record<string, string>>,
): { id: string; name: string; is_active: boolean }[] {
  return module.map((m) => ({ id: m.id, name: dbNamen[m.id] ?? m.name, is_active: m.active }));
}

export type FilterWert = 'alle' | 'geplant' | 'fehler' | 'erledigt';

export function filtereWechsel<T extends Pick<WechselEintrag, 'status'>>(eintraege: readonly T[], filter: FilterWert): T[] {
  return eintraege.filter((e) => {
    if (filter === 'alle') return true;
    if (filter === 'geplant') return e.status === 'geplant';
    if (filter === 'fehler') return e.status === 'fehler';
    if (filter === 'erledigt') return e.status === 'erledigt';
    return true;
  });
}

/**
 * Welcher Knopf eine Zeile bekommt – exakt die bisherigen Bedingungen:
 * Ausführen (geplant + fällig ≤ heute), Erneut versuchen (fehler + versuche < 3 → ausführen;
 * übersprungen → neu planen + ausführen), Rückgängig (erledigt). Die Fälle schließen sich nach Status aus.
 */
export type WechselAktion = 'ausfuehren' | 'erneut' | 'erneut_uebersprungen' | 'rueckgaengig';

export function wechselAktion(e: Pick<WechselEintrag, 'status' | 'faellig_am' | 'versuche'>, heute: string): WechselAktion | null {
  if (e.status === 'geplant' && e.faellig_am <= heute) return 'ausfuehren';
  if (e.status === 'fehler' && e.versuche < 3) return 'erneut';
  if (e.status === 'uebersprungen') return 'erneut_uebersprungen';
  if (e.status === 'erledigt') return 'rueckgaengig';
  return null;
}

export const SCHWELLE_MIN = 50;
export const SCHWELLE_MAX = 100;
export const SCHWELLE_SCHRITT = 5;

/**
 * Stepper −/+ der Auto-Zuordnungs-Schwelle in Prozent: wie das bisherige Zahlenfeld (min 50, max 100, step 5) –
 * ein Wert außerhalb des 5er-Rasters springt auf den nächsten Rasterwert in Schrittrichtung.
 */
export function schwelleSchritt(prozent: number, richtung: 1 | -1): number {
  const basis = richtung === 1
    ? Math.floor(prozent / SCHWELLE_SCHRITT) * SCHWELLE_SCHRITT + SCHWELLE_SCHRITT
    : Math.ceil(prozent / SCHWELLE_SCHRITT) * SCHWELLE_SCHRITT - SCHWELLE_SCHRITT;
  return Math.min(SCHWELLE_MAX, Math.max(SCHWELLE_MIN, basis));
}

/** Gespeicherte Schwelle (0–1) als ganze Prozent für die Anzeige. */
export const schwelleProzent = (wert: number): number => Math.round(wert * 100);
